import type { CSSProperties } from 'react';

/**
 * Shared Recharts tooltip styling.
 *
 * Recharts renders its tooltip on a default white surface with no awareness of
 * the app theme: on the dark theme the label text becomes near-invisible and
 * the series colours (tuned for a dark ground) lose all contrast. Every
 * Recharts `<Tooltip>` should spread these props so the surface follows
 * tokens.css in both modes.
 *
 * CSS variables are safe here — the tooltip is an HTML div, not SVG, so
 * `var()` resolves normally.
 */
export const chartTooltipProps: {
  contentStyle: CSSProperties;
  labelStyle: CSSProperties;
  itemStyle: CSSProperties;
  cursor: { fill: string; stroke: string };
} = {
  // Reason: the default hover cursor is an opaque #ccc rect that glares on the
  // dark theme. SVG attributes don't resolve var(), so this is a literal that
  // reads as a subtle highlight on both themes.
  cursor: { fill: 'rgba(148, 163, 184, 0.12)', stroke: 'rgba(148, 163, 184, 0.35)' },
  contentStyle: {
    background: 'var(--surface-solid)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
  },
  labelStyle: { color: 'var(--text-secondary)', fontWeight: 600 },
  // Reason: without an explicit item colour Recharts falls back to the series
  // stroke — several series use soft fills that fail contrast on any surface.
  itemStyle: { color: 'var(--text)' },
};
